import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const appendQueryParams = (queryParams, params = {}) => {
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== "" && item != null && item !== undefined) {
          queryParams.append(`${key}[]`, String(item));
        }
      });
      return;
    }
    if (value !== "" && value != null && value !== undefined) {
      queryParams.append(key, String(value));
    }
  });
};

const itemTaxableFormValue = (item) => {
  if (item?.taxable === true || item?.taxable === 1 || item?.taxable === "1") {
    return "1";
  }
  return "0";
};

/** Build multipart body for POST /credit-notes/manual */
export function buildManualCreditNoteFormData(form) {
  const fd = new FormData();

  const append = (key, value) => {
    if (value !== null && value !== undefined && value !== "") {
      fd.append(key, String(value));
    }
  };

  [
    "sales_invoice_id",
    "customer_type",
    "customer_id",
    "winch_branch_id",
    "responsible_name",
    "issue_date",
    "due_at",
    "notification_type",
    "tax_calculation",
    "cost_center_account_id",
    "invoice_system",
    "description",
    "notes",
    "comment",
    "status",
  ].forEach((key) => append(key, form[key]));

  (form.items || []).forEach((item, index) => {
    const isCompensation = Boolean(item?.is_compensation);
    [
      "order_id",
      "supply_date",
      "description",
      "from_city_id",
      "to_city_id",
      "quantity",
      "unit_price",
      "tax_percentage",
    ].forEach((field) => {
      const value =
        field === "order_id"
          ? item.order_id ?? item.trip_request_id ?? item.request_id
          : item[field];
      if (value !== "" && value != null) {
        fd.append(`items[${index}][${field}]`, String(value));
      } else if (field === "from_city_id" || field === "to_city_id") {
        fd.append(`items[${index}][${field}]`, "");
      }
    });

    const compensationId = item.compensation_id;
    if (compensationId !== "" && compensationId != null) {
      fd.append(`items[${index}][compensation_id]`, String(compensationId));
    }

    if (isCompensation) {
      const categoryId =
        item.compensation_category_id || item.service_type_id || "";
      if (categoryId !== "" && categoryId != null) {
        fd.append(
          `items[${index}][compensation_category_id]`,
          String(categoryId)
        );
      }
    } else if (item.service_type_id !== "" && item.service_type_id != null) {
      fd.append(
        `items[${index}][service_type_id]`,
        String(item.service_type_id)
      );
    }

    fd.append(`items[${index}][taxable]`, itemTaxableFormValue(item));
  });

  return fd;
}

export const fetchCreditNotes = createAsyncThunk(
  "credit_notes/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      appendQueryParams(queryParams, { page: 1, per_page: 20, ...params });
      const qs = queryParams.toString();
      const response = await axios.get(
        `${BASE_URL}/credit-notes${qs ? `?${qs}` : ""}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load data";
      return rejectWithValue(message);
    }
  }
);

export const fetchCreditNoteDetail = createAsyncThunk(
  "credit_notes/fetchDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/credit-notes/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data?.data ?? null;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to load credit note";
      return rejectWithValue(message);
    }
  }
);

export const createManualCreditNote = createAsyncThunk(
  "credit_notes/createManual",
  async (data, { rejectWithValue }) => {
    try {
      const isFormData =
        typeof FormData !== "undefined" && data instanceof FormData;
      const response = await axios.post(
        `${BASE_URL}/credit-notes/manual`,
        data,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            ...(isFormData
              ? { "Content-Type": "multipart/form-data" }
              : { "Content-Type": "application/json" }),
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to create credit note";
      return rejectWithValue(message);
    }
  }
);

/** Build multipart body for POST /credit-notes/full */
export function buildFullCreditNoteFormData({
  sales_invoice_id,
  issue_date,
  status,
}) {
  const fd = new FormData();
  fd.append("sales_invoice_id", String(sales_invoice_id));
  fd.append("issue_date", String(issue_date));
  fd.append("status", String(status));
  return fd;
}

/** Build multipart body for POST /credit-notes/partial */
export function buildPartialCreditNoteFormData({
  sales_invoice_id,
  invoice_item_ids,
  status,
  issue_date,
}) {
  const fd = new FormData();
  fd.append("sales_invoice_id", String(sales_invoice_id));
  fd.append("status", String(status));
  if (issue_date) {
    fd.append("issue_date", String(issue_date));
  }
  (invoice_item_ids || []).forEach((id) => {
    if (id !== "" && id != null) {
      fd.append("invoice_item_ids[]", String(id));
    }
  });
  return fd;
}

const authMultipartHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "multipart/form-data",
  Lang: i18n.language,
});

export const createFullCreditNote = createAsyncThunk(
  "credit_notes/createFull",
  async (payload, { rejectWithValue }) => {
    try {
      const data =
        typeof FormData !== "undefined" && payload instanceof FormData
          ? payload
          : buildFullCreditNoteFormData(payload);
      const response = await axios.post(`${BASE_URL}/credit-notes/full`, data, {
        headers: authMultipartHeaders(),
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to create full credit note";
      return rejectWithValue(message);
    }
  }
);

export const createPartialCreditNote = createAsyncThunk(
  "credit_notes/createPartial",
  async (payload, { rejectWithValue }) => {
    try {
      const data =
        typeof FormData !== "undefined" && payload instanceof FormData
          ? payload
          : buildPartialCreditNoteFormData(payload);
      const response = await axios.post(
        `${BASE_URL}/credit-notes/partial`,
        data,
        {
          headers: authMultipartHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to create partial credit note";
      return rejectWithValue(message);
    }
  }
);

/** Coerce order_id to a plain integer string (never JSON array). */
const normalizeOrderIdForApi = (raw) => {
  if (raw === "" || raw == null) return "";
  if (Array.isArray(raw)) {
    const first = raw.find((v) => v !== "" && v != null);
    return first != null ? String(first).replace(/\D/g, "") : "";
  }
  let s = String(raw).trim();
  if (s.startsWith("[")) {
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) {
        const first = parsed.find((v) => v !== "" && v != null);
        return first != null ? String(first).replace(/\D/g, "") : "";
      }
    } catch {
      /* fall through */
    }
  }
  return s.replace(/\D/g, "");
};

/**
 * Build multipart body for POST /credit-notes/{id}/items (draft update).
 * Payload fields only:
 *   items[i][order_id], items[i][quantity], items[i][unit_price],
 *   items[i][description] (optional), items[i][compensation_id] (optional)
 */
export function buildUpdateCreditNoteItemsFormData(form) {
  const fd = new FormData();

  (form.items || []).forEach((item, index) => {
    const orderId = normalizeOrderIdForApi(
      item?.order_id ?? item?.trip_request_id ?? item?.request_id
    );
    if (orderId) {
      fd.append(`items[${index}][order_id]`, orderId);
    }

    if (item?.quantity !== "" && item?.quantity != null) {
      fd.append(`items[${index}][quantity]`, String(item.quantity));
    }
    if (item?.unit_price !== "" && item?.unit_price != null) {
      fd.append(`items[${index}][unit_price]`, String(item.unit_price));
    }
    if (item?.description !== "" && item?.description != null) {
      fd.append(`items[${index}][description]`, String(item.description));
    }

    const compensationId = item?.compensation_id;
    if (compensationId !== "" && compensationId != null) {
      fd.append(
        `items[${index}][compensation_id]`,
        String(compensationId).replace(/\D/g, "") || String(compensationId)
      );
    }
  });

  return fd;
}

export const updateCreditNoteItems = createAsyncThunk(
  "credit_notes/updateItems",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const data =
        typeof FormData !== "undefined" && formData instanceof FormData
          ? formData
          : buildUpdateCreditNoteItemsFormData(formData);
      const response = await axios.post(
        `${BASE_URL}/credit-notes/${id}/items`,
        data,
        {
          headers: authMultipartHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to update credit note items";
      return rejectWithValue(message);
    }
  }
);

/** POST /credit-notes/publish — body: id (string) */
export const publishCreditNote = createAsyncThunk(
  "credit_notes/publish",
  async (noteId, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("id", String(noteId));

      const response = await axios.post(
        `${BASE_URL}/credit-notes/publish`,
        formData,
        {
          headers: authMultipartHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to publish credit note";
      return rejectWithValue(message);
    }
  }
);

const CreditNotesSlice = createSlice({
  name: "credit_notes",
  initialState: {
    credit_notes: [],
    record: null,
    isLoading: false,
    detailLoading: false,
    detailError: null,
    updateLoading: false,
    updateSuccess: null,
    publishLoading: false,
    error: null,
    success: null,
    pagination: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
      state.updateSuccess = null;
    },
    clearCreditNoteDetail: (state) => {
      state.record = null;
      state.detailLoading = false;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCreditNotes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCreditNotes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.credit_notes = action.payload?.data?.data || [];
        state.pagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchCreditNotes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
        state.credit_notes = [];
        state.pagination = null;
      })
      .addCase(fetchCreditNoteDetail.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchCreditNoteDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.record = action.payload;
      })
      .addCase(fetchCreditNoteDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload || "Failed to load credit note";
      })
      .addCase(createManualCreditNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createManualCreditNote.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Created successfully";
      })
      .addCase(createManualCreditNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to create credit note";
      })
      .addCase(createFullCreditNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createFullCreditNote.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Created successfully";
      })
      .addCase(createFullCreditNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to create full credit note";
      })
      .addCase(createPartialCreditNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createPartialCreditNote.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Created successfully";
      })
      .addCase(createPartialCreditNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to create partial credit note";
      })
      .addCase(updateCreditNoteItems.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.updateSuccess = null;
      })
      .addCase(updateCreditNoteItems.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess =
          action.payload?.message || "Updated successfully";
        if (action.payload?.data) {
          state.record = action.payload.data;
        }
      })
      .addCase(updateCreditNoteItems.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload || "Failed to update credit note items";
      })
      .addCase(publishCreditNote.pending, (state) => {
        state.publishLoading = true;
        state.error = null;
      })
      .addCase(publishCreditNote.fulfilled, (state, action) => {
        state.publishLoading = false;
        state.success =
          action.payload?.message || "Published successfully";
        if (action.payload?.data) {
          state.record = action.payload.data;
        }
      })
      .addCase(publishCreditNote.rejected, (state, action) => {
        state.publishLoading = false;
        state.error = action.payload || "Failed to publish credit note";
      });
  },
});

export const { clearState, clearCreditNoteDetail } = CreditNotesSlice.actions;
export default CreditNotesSlice.reducer;
